import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Check, X, Database, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { UserEntitlements } from '@/lib/entitlements';

interface AIContextDebugBadgeProps {
  entitlements: UserEntitlements | null;
  isAdmin?: boolean;
}

const MODULE_LABELS: Array<{ key: keyof UserEntitlements; label: string }> = [
  { key: 'hasNutrition', label: 'Nutrition' },
  { key: 'hasSleep', label: 'Sleep' },
  { key: 'hasDreams', label: 'Dreams' },
  { key: 'hasInflammation', label: 'IL' },
  { key: 'hasWhoop', label: 'WHOOP' },
  { key: 'hasCheckIns', label: 'CheckIns' },
  { key: 'hasJournal', label: 'Journal' },
  { key: 'hasMeditations', label: 'Meditations' },
];

/**
 * Dev-only debug badge. Only renders when:
 * 1. import.meta.env.DEV === true (stripped from production builds)
 * 2. isAdmin === true
 */
export default function AIContextDebugBadge({ entitlements, isAdmin }: AIContextDebugBadgeProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Gate: DEV mode only + admin only
  if (!import.meta.env.DEV || !isAdmin || !entitlements) return null;

  const unlocked = MODULE_LABELS.filter(({ key }) => entitlements[key] as boolean);
  const locked = MODULE_LABELS.filter(({ key }) => !(entitlements[key] as boolean));

  return (
    <div className="space-y-1 p-2 bg-muted/50 rounded-md border border-border text-xs">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsExpanded(!isExpanded)}
        className="h-auto p-0 gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <Database className="h-3 w-3" />
        <span>Show Debug</span>
        <Badge variant="outline" className="text-[10px] px-1 ml-1">{entitlements.tier}</Badge>
      </Button>

      {isExpanded && (
        <div className="space-y-1 pt-1">
          <div className="flex flex-wrap gap-1">
            <span className="text-muted-foreground font-medium">DataAvailable:</span>
            {MODULE_LABELS.map(({ label }) => (
              <span key={label} className="text-blue-600 dark:text-blue-400 inline-flex items-center gap-0.5">
                <Check className="h-3 w-3" />{label}
              </span>
            ))}
          </div>

          <div className="flex flex-wrap gap-1">
            <span className="text-muted-foreground font-medium">OutputsUnlocked:</span>
            {unlocked.map(({ label }) => (
              <span key={label} className="text-green-600 dark:text-green-400 inline-flex items-center gap-0.5">
                <Check className="h-3 w-3" />{label}
              </span>
            ))}
            {unlocked.length === 0 && <span className="text-muted-foreground/50">none</span>}
          </div>

          <div className="flex flex-wrap gap-1">
            <span className="text-muted-foreground font-medium">OutputsLocked:</span>
            {locked.map(({ label }) => (
              <span key={label} className="text-red-500 dark:text-red-400 inline-flex items-center gap-0.5">
                <X className="h-3 w-3" />{label}
              </span>
            ))}
            {locked.length === 0 && <span className="text-muted-foreground/50">none</span>}
          </div>

          <div className="flex flex-wrap gap-1">
            <span className="text-muted-foreground font-medium">LockedSignalsPresent:</span>
            {locked.length > 0 ? locked.map(({ label }) => (
              <span key={label} className="text-amber-600 dark:text-amber-400 inline-flex items-center gap-0.5">
                <AlertTriangle className="h-3 w-3" />{label}
              </span>
            )) : <span className="text-muted-foreground/50">none (all unlocked)</span>}
          </div>
        </div>
      )}
    </div>
  );
}
